<script setup>
import { ref, watch, onMounted, onUnmounted, nextTick } from 'vue';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

const props = defineProps({
  latitude: { type: [Number, String], default: '' },
  longitude: { type: [Number, String], default: '' },
});

const emit = defineEmits(['update:latitude', 'update:longitude']);

// Fix Leaflet default icon URLs for Vite bundler
// eslint-disable-next-line no-underscore-dangle
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

const DEFAULT_CENTER = [20.5888, -100.3899]; // Queretaro
const DEFAULT_ZOOM = 14;
const COORD_THRESHOLD = 0.00001;

const mapContainer = ref(null);
let map = null;
let marker = null;

const updateCoords = (lat, lng) => {
  const roundedLat = Math.round(lat * 10000000) / 10000000;
  const roundedLng = Math.round(lng * 10000000) / 10000000;
  emit('update:latitude', roundedLat);
  emit('update:longitude', roundedLng);
};

const onMapClick = e => {
  const { lat, lng } = e.latlng;
  marker.setLatLng([lat, lng]);
  updateCoords(lat, lng);
};

const onMarkerDragEnd = () => {
  const { lat, lng } = marker.getLatLng();
  updateCoords(lat, lng);
};

const geocode = async address => {
  const encoded = encodeURIComponent(address);
  const url = `https://nominatim.openstreetmap.org/search?q=${encoded}&format=json&countrycodes=mx&viewbox=-100.6,20.2,-99.8,21.0&bounded=1&limit=1&email=admin@ceaqueretaro.gob.mx`;

  const res = await fetch(url);
  const data = await res.json();

  if (!data.length) return false;

  const lat = parseFloat(data[0].lat);
  const lng = parseFloat(data[0].lon);
  marker.setLatLng([lat, lng]);
  map.setView([lat, lng], 16);
  updateCoords(lat, lng);
  return true;
};

defineExpose({ geocode });

// Watch prop changes to move marker (avoid loops with threshold)
watch(
  () => [props.latitude, props.longitude],
  ([newLat, newLng]) => {
    if (!marker || !map) return;
    const lat = parseFloat(newLat);
    const lng = parseFloat(newLng);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;

    const current = marker.getLatLng();
    if (
      Math.abs(current.lat - lat) > COORD_THRESHOLD ||
      Math.abs(current.lng - lng) > COORD_THRESHOLD
    ) {
      marker.setLatLng([lat, lng]);
      map.setView([lat, lng], map.getZoom());
    }
  }
);

onMounted(() => {
  nextTick(() => {
    const lat = parseFloat(props.latitude) || DEFAULT_CENTER[0];
    const lng = parseFloat(props.longitude) || DEFAULT_CENTER[1];

    map = L.map(mapContainer.value).setView([lat, lng], DEFAULT_ZOOM);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);

    marker = L.marker([lat, lng], { draggable: true }).addTo(map);
    marker.on('dragend', onMarkerDragEnd);
    map.on('click', onMapClick);

    // Fix map rendering inside modal transition
    setTimeout(() => map.invalidateSize(), 300);
  });
});

onUnmounted(() => {
  if (map) map.remove();
});
</script>

<template>
  <div ref="mapContainer" class="h-[300px] w-full rounded-lg z-0" />
</template>
